import { useEffect, useState } from 'react';
import { Outlet, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import {
  AssistantBridgeControlPlaneSection,
  AssistantWorkflowStudioPage,
  PaymentsControlPlaneSection,
  SettingsBundleProvider,
  UiConsultantStudioPage,
} from '@triedge/staff-settings-ui';
import {
  CreditCard,
  CalendarCheck,
  Link2,
  Plug,
  Paintbrush2,
  Sparkles,
  Workflow,
} from 'lucide-react';
import { ModuleSectionNav } from '../../components/navigation/ModuleSectionNav';
import { settingsFeatureBundle } from '../../settings/featureBundle';
import {
  buildSuperAdminAssistantWorkflowPath,
  buildSuperAdminUiConsultantPath,
  SUPER_ADMIN_CONTROL_PLANE_ATTENDANCE,
  SUPER_ADMIN_CONTROL_PLANE_ASSISTANT_BRIDGE,
  SUPER_ADMIN_CONTROL_PLANE_ASSISTANT_WORKFLOWS,
  SUPER_ADMIN_CONTROL_PLANE_DC_CLEANUP,
  SUPER_ADMIN_CONTROL_PLANE_INTEGRATIONS,
  SUPER_ADMIN_CONTROL_PLANE_PAYMENTS,
  SUPER_ADMIN_CONTROL_PLANE_ROOT,
  SUPER_ADMIN_CONTROL_PLANE_UI_CONSULTANT,
} from './paths';
import Integrations from '../school-settings-v2/screens/Integrations';
import { DcCleanupPage } from './DcCleanupPage';
import { useYstnjAttendanceFeatureBundleState } from '../../attendance/featureBundle';
import type {
  AttendanceAuditEvent,
  AttendanceControlPlaneConfig,
  AttendanceImportStatus,
} from '@triedge/attendance-ui';

const CONTROL_PLANE_NAV_ITEMS = [
  {
    id: 'payments',
    label: 'Payments',
    to: SUPER_ADMIN_CONTROL_PLANE_PAYMENTS,
    icon: CreditCard,
  },
  {
    id: 'integrations',
    label: 'Integrations',
    to: SUPER_ADMIN_CONTROL_PLANE_INTEGRATIONS,
    icon: Plug,
  },
  {
    id: 'attendance',
    label: 'Attendance',
    to: SUPER_ADMIN_CONTROL_PLANE_ATTENDANCE,
    icon: CalendarCheck,
  },
  {
    id: 'assistant-bridge',
    label: 'Assistant Bridge',
    to: SUPER_ADMIN_CONTROL_PLANE_ASSISTANT_BRIDGE,
    icon: Link2,
  },
  {
    id: 'assistant-workflows',
    label: 'Workflows',
    to: buildSuperAdminAssistantWorkflowPath('new'),
    icon: Workflow,
  },
  {
    id: 'ui-consultant',
    label: 'UI Consultant',
    to: buildSuperAdminUiConsultantPath(),
    icon: Paintbrush2,
  },
  {
    id: 'dc-cleanup',
    label: 'DC Cleanup',
    to: SUPER_ADMIN_CONTROL_PLANE_DC_CLEANUP,
    icon: Sparkles,
  },
] as const;

const resolveActiveSectionId = (pathname: string): string => {
  if (pathname.startsWith(SUPER_ADMIN_CONTROL_PLANE_INTEGRATIONS)) {
    return 'integrations';
  }
  if (pathname.startsWith(SUPER_ADMIN_CONTROL_PLANE_ATTENDANCE)) {
    return 'attendance';
  }
  if (pathname.startsWith(SUPER_ADMIN_CONTROL_PLANE_ASSISTANT_BRIDGE)) {
    return 'assistant-bridge';
  }
  if (pathname.startsWith(SUPER_ADMIN_CONTROL_PLANE_ASSISTANT_WORKFLOWS)) {
    return 'assistant-workflows';
  }
  if (pathname.startsWith(SUPER_ADMIN_CONTROL_PLANE_UI_CONSULTANT)) {
    return 'ui-consultant';
  }
  if (pathname.startsWith(SUPER_ADMIN_CONTROL_PLANE_DC_CLEANUP)) {
    return 'dc-cleanup';
  }
  return 'payments';
};

const ControlPlaneLayout = () => {
  const location = useLocation();
  return (
    <section
      data-testid="super-administrator-control-plane-root"
      data-extracted-from="owned:ystnj/super-administrator/control-plane"
      className="min-h-full min-w-0 antialiased"
    >
      <div className="mx-auto min-h-0 w-full max-w-[1440px] space-y-5">
        <div className="rounded-lg border border-border bg-white px-6 py-6 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
            Super Administrator
          </p>
          <h1 className="mt-2 text-2xl font-semibold text-irongrey">
            Control Plane
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-text-secondary">
            Operator-only tooling for integrations, payment configuration,
            assistant bridge diagnostics, workflow studio, and governed UI
            consultation.
          </p>
        </div>
        <ModuleSectionNav
          items={[...CONTROL_PLANE_NAV_ITEMS]}
          activeId={resolveActiveSectionId(location.pathname)}
          ariaLabel="Super administrator control-plane sections"
          testIds={{ itemPrefix: 'super-administrator-control-plane-tab' }}
        />
        <div
          className="pb-20"
          data-testid="super-administrator-control-plane-visual-surface"
        >
          <Outlet />
        </div>
      </div>
    </section>
  );
};

const PaymentsRoute = () => (
  <SettingsBundleProvider value={settingsFeatureBundle}>
    <PaymentsControlPlaneSection
      mode="advanced"
      routePath={SUPER_ADMIN_CONTROL_PLANE_PAYMENTS}
    />
  </SettingsBundleProvider>
);

const AssistantBridgeRoute = () => (
  <SettingsBundleProvider value={settingsFeatureBundle}>
    <AssistantBridgeControlPlaneSection
      mode="advanced"
      routePath={SUPER_ADMIN_CONTROL_PLANE_ASSISTANT_BRIDGE}
    />
  </SettingsBundleProvider>
);

type AttendanceControlPlaneState = {
  config: AttendanceControlPlaneConfig | null;
  auditEvents: AttendanceAuditEvent[];
  importStatus: AttendanceImportStatus | null;
  isLoading: boolean;
  error: string | null;
};

const AttendanceControlPlaneRoute = () => {
  const { bundle, error: bundleError, isLoading: bundleIsLoading } =
    useYstnjAttendanceFeatureBundleState();
  const [state, setState] = useState<AttendanceControlPlaneState>({
    config: null,
    auditEvents: [],
    importStatus: null,
    isLoading: false,
    error: null,
  });

  useEffect(() => {
    if (!bundle) return;

    let active = true;
    setState((current) => ({ ...current, isLoading: true, error: null }));
    void Promise.all([
      bundle.transport.query('getAttendanceControlPlaneConfig', {
        input: bundle.runtimeScope,
      }),
      bundle.transport.query('listAttendanceAuditEvents', {
        input: { ...bundle.runtimeScope, limit: 5 },
      }),
      bundle.transport.query('getAttendanceImportStatus', {
        input: { ...bundle.runtimeScope, importId: 'latest-attendance-import' },
      }),
    ])
      .then(([config, audit, importStatus]) => {
        if (!active) return;
        setState({
          config,
          auditEvents: audit.events,
          importStatus,
          isLoading: false,
          error: null,
        });
      })
      .catch((reason) => {
        if (!active) return;
        setState((current) => ({
          ...current,
          isLoading: false,
          error:
            reason instanceof Error
              ? reason.message
              : 'Unable to load attendance control-plane data.',
        }));
      });

    return () => {
      active = false;
    };
  }, [bundle]);

  if (bundleIsLoading || state.isLoading) {
    return (
      <section className="rounded-lg border border-border bg-white p-6 shadow-sm">
        <p className="text-sm text-text-secondary">Loading attendance control plane.</p>
      </section>
    );
  }

  if (!bundle) {
    return (
      <section className="rounded-lg border border-border bg-white p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-irongrey">Attendance Control Plane</h2>
        <p className="mt-2 text-sm text-text-secondary">
          {bundleError || 'Select an active school context before configuring attendance.'}
        </p>
      </section>
    );
  }

  const config = state.config;

  return (
    <section
      className="space-y-4"
      data-testid="attendance-control-plane-route"
      data-extracted-from="owned:ystnj/super-administrator/control-plane/attendance"
    >
      <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
          Attendance
        </p>
        <h2 className="mt-2 text-xl font-semibold text-irongrey">
          Control Plane
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-text-secondary">
          Super-administrator-only configuration, import status, and audit
          visibility for the Attendance v1.2 records workflow, including
          divisions, classrooms, periods, rosters, teacher assignments, and
          trailing 30-day historic attendance seed data.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-md border border-border bg-white px-3 py-2 text-sm font-semibold text-irongrey hover:bg-muted"
          >
            Import Attendance CSV
          </button>
          <button
            type="button"
            className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary-hover"
          >
            Save Configuration
          </button>
        </div>
        {state.error ? (
          <p className="mt-3 rounded-md border border-[#fe2c3b]/30 bg-[#fe2c3b]/10 px-3 py-2 text-sm text-[#9f1239]">
            {state.error}
          </p>
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-irongrey">Configuration</p>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Divisions</dt>
              <dd className="font-semibold text-irongrey">{config?.divisions.length ?? 0}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Classrooms</dt>
              <dd className="font-semibold text-irongrey">{config?.classrooms.length ?? 0}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Periods</dt>
              <dd className="font-semibold text-irongrey">{config?.periods.length ?? 0}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Rosters</dt>
              <dd className="font-semibold text-irongrey">
                {config?.rosterMemberships.length ?? 0}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Teacher assignments</dt>
              <dd className="font-semibold text-irongrey">
                {config?.classrooms.filter((classroom) => classroom.teacherId || classroom.teacherName).length ?? 0}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Default</dt>
              <dd className="font-semibold capitalize text-irongrey">
                {config?.schoolCalendar.defaultStatus ?? 'present'}
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-irongrey">Import Status</p>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Latest status</dt>
              <dd className="font-semibold capitalize text-irongrey">
                {state.importStatus?.status ?? 'pending'}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Processed</dt>
              <dd className="font-semibold text-irongrey">
                {state.importStatus?.processedRows ?? 0}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Failed</dt>
              <dd className="font-semibold text-irongrey">
                {state.importStatus?.failedRows ?? 0}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Trailing 30-day history</dt>
              <dd className="font-semibold text-irongrey">
                Historic attendance seed ready
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-irongrey">Audit Events</p>
          <div className="mt-4 space-y-3">
            {state.auditEvents.length ? (
              state.auditEvents.map((event) => (
                <div key={event.auditId} className="rounded-md border border-border px-3 py-2">
                  <p className="text-sm font-semibold capitalize text-irongrey">
                    {event.action}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {event.actorId} / {event.sourceSurface}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-sm text-text-secondary">No audit events recorded.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const AssistantWorkflowRoute = () => {
  const params = useParams();
  return (
    <AssistantWorkflowStudioPage
      bundle={settingsFeatureBundle}
      workflowId={String(params.workflowId ?? 'new')}
      workflowRouteBasePath={SUPER_ADMIN_CONTROL_PLANE_ASSISTANT_WORKFLOWS}
    />
  );
};

const UiConsultantRoute = () => {
  const params = useParams();
  return (
    <UiConsultantStudioPage
      bundle={settingsFeatureBundle}
      sessionId={String(params.sessionId ?? 'new')}
      uiConsultantRouteBasePath={SUPER_ADMIN_CONTROL_PLANE_UI_CONSULTANT}
      uiConsultationContext={{
        routeBase: SUPER_ADMIN_CONTROL_PLANE_ROOT,
        section: 'control-plane',
        defaultTargetRoute: SUPER_ADMIN_CONTROL_PLANE_PAYMENTS,
        objectTreeRoute: SUPER_ADMIN_CONTROL_PLANE_UI_CONSULTANT,
        objectTreeSummary:
          'Super-administrator control-plane studio with route-aware governance context.',
        runtimeSummary:
          'Super-administrator control-plane studio embedded under the operator route family.',
      }}
    />
  );
};

export const SuperAdminControlPlaneRoutes = () => (
  <Routes>
    <Route element={<ControlPlaneLayout />}>
      <Route index element={<Navigate to="payments" replace />} />
      <Route path="payments" element={<PaymentsRoute />} />
      <Route path="integrations" element={<Integrations />} />
      <Route path="attendance" element={<AttendanceControlPlaneRoute />} />
      <Route path="assistant-bridge" element={<AssistantBridgeRoute />} />
      <Route path="assistant-workflows/:workflowId" element={<AssistantWorkflowRoute />} />
      <Route path="ui-consultant" element={<UiConsultantRoute />} />
      <Route path="ui-consultant/:sessionId" element={<UiConsultantRoute />} />
      <Route path="dc-cleanup" element={<DcCleanupPage />} />
      <Route path="*" element={<Navigate to="payments" replace />} />
    </Route>
  </Routes>
);
